import React from 'react';
import { Loading } from '@bumble/app/components/Loading';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';

interface UserAgentLinkProps {
  onSubmit: () => void;
  userAgentId: string;
}
export const UserAgentLink = ({ onSubmit, userAgentId }: UserAgentLinkProps) => {
  const api = useApi();

  const { isLoading, data } = useQuery(
    ['userAgentsUnlinks'],
    api.userAgentGetUnlinkedEpApiV1UserAgentGetUnlinkedEpGet.bind(null, {
      user_agent_id: userAgentId,
    }),
    { enabled: true },
  );

  const [endpoint, setEndpoint] = React.useState<string>('');

  const handleSetEndpoint = (event: SelectChangeEvent) => {
    setEndpoint(event.target.value as string);
  };

  const linkUserAgent = useMutation(api.linkUserAgentToEpApiV1LinkUserAgentToEpPost, {
    onSuccess() {
      toast.success(`Linked user agent ${userAgentId} to endpoint ${endpoint} successfully.`);
      onSubmit();
    },
    onError: apiError,
  });
  const handleSubmit = () => {
    linkUserAgent.mutate({
      user_agent_id: userAgentId,
      ep_id: endpoint,
    });
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Box pt={2}>
      {!isLoading && data?.data && data?.data.length > 0 ? (
        <FormControl fullWidth>
          <InputLabel id="endpoint-label">Endpoint(s)</InputLabel>
          <Select
            labelId="endpoint-label"
            id="endpoint"
            label="Endpoint(s)"
            value={endpoint}
            onChange={handleSetEndpoint}
          >
            {data?.data.map((ep) => (
              <MenuItem value={ep} key={ep}>
                {ep}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <Alert severity="error">No endpoints available to link to user agent.</Alert>
      )}
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button variant="contained" onClick={handleSubmit} disabled={endpoint === '' || !endpoint}>
          Link User Agent
        </Button>
      </Box>
    </Box>
  );
};
UserAgentLink.displayName = 'UserAgentLink';
